import SelectDropDown from '@/components/Common/Selects/SelectDropDown';
import DoughnutChart from '@/components/Ui/DoughnutChart/DoughnutChart';
import LegendDoughnutChart from '@/components/Ui/DoughnutChart/LegendDoughnutChart';
import { useState, useMemo, memo } from 'react';

interface DataItem {
  [key: string]: string | number | undefined;
}

interface ChartProps {
  title: string;
  subtitle: string;
  data: DataItem[];
  colors: string[];
  filterOptions: { name: string; _id: string }[];
  labelKey: string;
  formatLabel?: (label: string) => string;
}

const ChartFilter = memo(
  ({
    filterChart,
    onFilterChange,
    filterOptions,
  }: {
    filterChart: string;
    onFilterChange: (value: string) => void;
    filterOptions: { name: string; _id: string }[];
  }) => {
    return (
      <SelectDropDown
        options={filterOptions}
        placeholder={filterOptions[0].name}
        handleChange={(option) => onFilterChange(option._id as string)}
        className="!text-white !text-xs"
        initialSelectedOptions={
          filterOptions.find((option) => option._id === filterChart) ||
          filterOptions[0]
        }
      />
    );
  },
);

ChartFilter.displayName = 'ChartFilter';

const StatisticsChart = memo(
  ({
    title,
    subtitle,
    data,
    colors,
    filterOptions,
    labelKey,
    formatLabel,
  }: ChartProps) => {
    const [filterChart, setFilterChart] = useState<string>(
      filterOptions[0]._id,
    );
    
    const chartLabels = useMemo(
      () => data.map((item) => item[filterChart]),
      [data, filterChart],
    );

    const legendItems = useMemo(() => {
      const totalValue = data.reduce(
        (sum, item) => sum + (item[filterChart] as number),
        0,
      );


      return data.map((item, index) => ({
        color: colors[index % colors.length],
        label: formatLabel
          ? formatLabel(item[labelKey] as string)
          : item[labelKey],
        value: item[filterChart],
        percentage:
          totalValue > 0

            ? Number(
                (((item[filterChart] as number) / totalValue) * 100).toFixed(1),
              )
            : 0,

      }));
    }, [data, colors, labelKey, filterChart, formatLabel]);


    const centerText = useMemo(() => ({ title, subtitle }), [title, subtitle]);

    return (
      <div className="relative bg-black w-64 flex justify-center h-[351px]">
        <div className="absolute right-[11px] top-[13px] z-10">
          <ChartFilter
            filterChart={filterChart}
            onFilterChange={setFilterChart}
            filterOptions={filterOptions}
          />
        </div>
        <div className="w-56 mt-4">
          <DoughnutChart
            centerText={centerText}
            labels={chartLabels as string[]}
            backgroundColor={colors}
          />
          <LegendDoughnutChart
            styleLegend="mt-[80px]"
            items={legendItems.map((item) => ({
              ...item,
              label: item.label as string,
              value: item.value as number,
              percentageOrSalesCount: item.percentage.toString()
            }))}
          />
        </div>
      </div>
    );
  },
);

StatisticsChart.displayName = 'StatisticsChart';

export default StatisticsChart;
